'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, X, Send, Loader2 } from 'lucide-react';
import { send } from '@emailjs/browser';
interface FeedbackFormData {
  name: string;
  email: string;
  message: string;
}
export default function FeedbackButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState<FeedbackFormData>({
    name: '',
    email: '',
    message: ''
  });
  const [errors, setErrors] = useState<Partial<FeedbackFormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const openModal = () => setIsOpen(true);
  const closeModal = () => {
    setIsOpen(false);
    // Reset form state when closing
    setTimeout(() => {
      setFormData({
        name: '',
        email: '',
        message: ''
      });
      setErrors({});
      setSubmitStatus('idle');
    }, 300);
  };
  const validateForm = (): boolean => {
    const newErrors: Partial<FeedbackFormData> = {};
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is not valid';
    }
    if (!formData.message) {
      newErrors.message = 'Message is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name as keyof FeedbackFormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // EmailJS configuration with provided credentials
    const serviceId = 'service_33f1gm3';
    const templateId = 'template_umd1op3';
    const publicKey = 'ECikutdcmOAlazMQT';
    try {
      const templateParams = {
        to_email: 'briliansetiaproject@gmail.com',
        from_name: formData.name || 'Anonymous',
        from_email: formData.email,
        message: formData.message
      };
      await send(serviceId, templateId, templateParams, publicKey);
      setSubmitStatus('success');

      // Reset form after successful submission
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          message: ''
        });
      }, 1500);
    } catch (error) {
      console.error('Failed to send email:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <>
      {/* Feedback Button */}
      <motion.button onClick={openModal} className="fixed z-40 bottom-8 left-8 flex items-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700" whileHover={{
      scale: 1.05
    }} whileTap={{
      scale: 0.95
    }} data-unique-id="da8ec91d-c870-401a-9689-f4d11dd16ba8" data-file-name="components/FeedbackButton.tsx">
        <MessageSquare size={20} />
        <span data-unique-id="6cd6a90e-e4f4-441c-b341-87e946b19763" data-file-name="components/FeedbackButton.tsx"><span className="editable-text" data-unique-id="8d03a816-9d3b-4e4c-91af-f9d61616de6f" data-file-name="components/FeedbackButton.tsx">Send Feedback</span></span>
      </motion.button>

      {/* Modal Overlay */}
      <AnimatePresence>
        {isOpen && <motion.div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={closeModal} data-unique-id="71e8476f-f999-453e-a75d-217e48b0b6f4" data-file-name="components/FeedbackButton.tsx" data-dynamic-text="true">
            {/* Modal Content */}
            <motion.div className="relative w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden" initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} onClick={e => e.stopPropagation()} data-unique-id="f25f3c5e-e101-46e5-9e77-ade2ac8a52bd" data-file-name="components/FeedbackButton.tsx" data-dynamic-text="true">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center" data-unique-id="214dd8ca-65d9-4d0e-8651-5995fec38630" data-file-name="components/FeedbackButton.tsx">
                <h3 className="text-xl font-semibold text-slate-800" data-unique-id="cb4e3137-e3ba-446e-8f44-415e767a6a85" data-file-name="components/FeedbackButton.tsx"><span className="editable-text" data-unique-id="b7802c6f-fa2f-4487-8e9c-b51a3c80f719" data-file-name="components/FeedbackButton.tsx">Send Feedback</span></h3>
                <button onClick={closeModal} className="p-2 rounded-full hover:bg-slate-100" data-unique-id="7f1b9119-d9f0-483e-bb91-f9ee16e79602" data-file-name="components/FeedbackButton.tsx">
                  <X size={20} className="text-slate-600" />
                </button>
              </div>
              
              {/* Modal Body */}
              <div className="px-6 py-5" data-unique-id="ac3a395f-0768-469e-b5b0-aa83c5ce28ee" data-file-name="components/FeedbackButton.tsx" data-dynamic-text="true">
                {submitStatus === 'success' ? <div className="py-6 text-center" data-unique-id="24052f94-6cd8-41d1-8014-dffc5b237ca4" data-file-name="components/FeedbackButton.tsx">
                    <div className="w-16 h-16 bg-green-100 rounded-full mx-auto flex items-center justify-center mb-4" data-unique-id="a69bc659-48c4-4445-9298-02a0ce42c6f7" data-file-name="components/FeedbackButton.tsx">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-500" viewBox="0 0 20 20" fill="currentColor" data-unique-id="19b9ae9c-f862-4bcd-8bee-13293a7275c3" data-file-name="components/FeedbackButton.tsx">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <h4 className="text-xl font-medium text-green-700" data-unique-id="ecb17c54-4245-435d-9c06-d4eb120133aa" data-file-name="components/FeedbackButton.tsx"><span className="editable-text" data-unique-id="99b4875b-dcca-4053-8d78-d42b738db3e4" data-file-name="components/FeedbackButton.tsx">Thank you!</span></h4>
                    <p className="mt-2 text-slate-600" data-unique-id="fa0a9a7f-2415-48a3-a0ab-b6d323b14d6b" data-file-name="components/FeedbackButton.tsx"><span className="editable-text" data-unique-id="0b046475-6a2d-4d57-a6a8-ada718c74361" data-file-name="components/FeedbackButton.tsx">Your feedback has been sent successfully.</span></p>
                  </div> : submitStatus === 'error' ? <div className="py-6 text-center" data-unique-id="04a5fccf-babf-4367-8087-9c2105f342c7" data-file-name="components/FeedbackButton.tsx">
                    <div className="w-16 h-16 bg-red-100 rounded-full mx-auto flex items-center justify-center mb-4" data-unique-id="0b8d103b-463f-4363-b62b-4e8687df2697" data-file-name="components/FeedbackButton.tsx">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-unique-id="ed399c33-4719-4234-b8c3-6814c315eff1" data-file-name="components/FeedbackButton.tsx">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h4 className="text-xl font-medium text-red-700" data-unique-id="31c4810a-efe5-47b3-a897-86de809e0f55" data-file-name="components/FeedbackButton.tsx"><span className="editable-text" data-unique-id="5d65bc1d-7e82-4937-81cd-8c03cc1c374e" data-file-name="components/FeedbackButton.tsx">Submission failed</span></h4>
                    <p className="mt-2 text-slate-600" data-unique-id="06341d37-2762-4cb3-943a-6fd845ad12fd" data-file-name="components/FeedbackButton.tsx"><span className="editable-text" data-unique-id="ac73670a-8835-4e6c-b2f9-6ebd34e125f3" data-file-name="components/FeedbackButton.tsx">There was an error sending your feedback. Please try again.</span></p>
                    <button onClick={() => setSubmitStatus('idle')} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700" data-unique-id="0fc52811-edb7-4d36-837b-b8618321293e" data-file-name="components/FeedbackButton.tsx"><span className="editable-text" data-unique-id="23843cfa-154d-44c7-9007-4dbe3d2a8cdc" data-file-name="components/FeedbackButton.tsx">
                      Try Again
                    </span></button>
                  </div> : <form onSubmit={handleSubmit} className="space-y-4" data-unique-id="1a6594ca-508c-4801-ba4a-67eb52b2e0df" data-file-name="components/FeedbackButton.tsx">
                    <div data-unique-id="d0ba3e28-2d71-4dcc-85bb-f066c5a56e77" data-file-name="components/FeedbackButton.tsx">
                      <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1" data-unique-id="8e810671-2189-4570-b2b7-9281c2599ca8" data-file-name="components/FeedbackButton.tsx"><span className="editable-text" data-unique-id="38006ea2-b9e1-4a76-9d09-29e45076895d" data-file-name="components/FeedbackButton.tsx">
                        Name (Optional)
                      </span></label>
                      <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} className="w-full px-4 py-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" placeholder="Your name" data-unique-id="0083fd88-cc98-42f5-82a1-fa919a07a9cc" data-file-name="components/FeedbackButton.tsx" />
                    </div>
                    
                    <div data-unique-id="4fa73169-c927-4137-a934-21cab21b16d5" data-file-name="components/FeedbackButton.tsx" data-dynamic-text="true">
                      <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1" data-unique-id="cf07f015-f2e7-4de3-bf09-fddb0fdcf8b5" data-file-name="components/FeedbackButton.tsx"><span className="editable-text" data-unique-id="8a11aae0-eaa4-4f73-bdc1-2a50a350575c" data-file-name="components/FeedbackButton.tsx">
                        Email </span><span className="text-red-500" data-unique-id="4c337bdd-c8df-4c13-8e1a-64ed2064a27a" data-file-name="components/FeedbackButton.tsx"><span className="editable-text" data-unique-id="2b1f8632-2323-4c93-b73c-1f74f9b2cd44" data-file-name="components/FeedbackButton.tsx">*</span></span>
                      </label>
                      <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className={`w-full px-4 py-3 border ${errors.email ? 'border-red-500 bg-red-50' : 'border-slate-300'} rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none`} placeholder="your.email@example.com" data-unique-id="66011773-b8d7-4a63-a01a-6f71ea1aee19" data-file-name="components/FeedbackButton.tsx" />
                      {errors.email && <p className="mt-1 text-sm text-red-600" data-unique-id="135a8717-361c-48af-824b-3e67693e58c4" data-file-name="components/FeedbackButton.tsx" data-dynamic-text="true">{errors.email}</p>}
                    </div>
                    
                    <div data-unique-id="fb4efaa8-639a-4009-bfb0-aa62764acb88" data-file-name="components/FeedbackButton.tsx" data-dynamic-text="true">
                      <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-1" data-unique-id="5d64cfca-f42a-46c4-b5f6-44b77b2b2a21" data-file-name="components/FeedbackButton.tsx"><span className="editable-text" data-unique-id="a47be6dc-f283-4484-87f8-184d2864827d" data-file-name="components/FeedbackButton.tsx">
                        Message </span><span className="text-red-500" data-unique-id="62c8d5d6-c8ca-4e2e-8c82-aa6b9e5468a8" data-file-name="components/FeedbackButton.tsx"><span className="editable-text" data-unique-id="0387b098-84cd-492a-ad84-bfa251cfba15" data-file-name="components/FeedbackButton.tsx">*</span></span>
                      </label>
                      <textarea id="message" name="message" value={formData.message} onChange={handleChange} rows={4} className={`w-full px-4 py-3 border ${errors.message ? 'border-red-500 bg-red-50' : 'border-slate-300'} rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none`} placeholder="Your feedback message..." data-unique-id="960324b3-4156-49d1-94c3-88b2351277b9" data-file-name="components/FeedbackButton.tsx" />
                      {errors.message && <p className="mt-1 text-sm text-red-600" data-unique-id="d7416a19-8fad-4a7a-a71a-b23c735efe9c" data-file-name="components/FeedbackButton.tsx" data-dynamic-text="true">{errors.message}</p>}
                    </div>
                    
                    <div className="pt-2" data-unique-id="854924c1-1f27-419f-864f-03a1d95f346c" data-file-name="components/FeedbackButton.tsx">
                      <button type="submit" disabled={isSubmitting} className="w-full py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center justify-center" data-unique-id="279920a7-fb15-4052-9a83-bce9844ee49e" data-file-name="components/FeedbackButton.tsx" data-dynamic-text="true">
                        {isSubmitting ? <>
                            <Loader2 size={18} className="animate-spin mr-2" />
                            Sending...
                          </> : <>
                            <Send size={18} className="mr-2" />
                            Submit Feedback
                          </>}
                      </button>
                    </div>
                  </form>}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}